import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { BellRing } from "lucide-react";
import { EASE } from "./Reveal";

const Bar = ({ className }) => <div className={`rounded-full ${className}`} />;

const FakeSite = ({ compact = false }) => (
  <div className={`flex h-full flex-col ${compact ? "gap-1.5 p-2.5" : "gap-3 p-4 sm:p-5"}`}>
    <div className="flex items-center justify-between">
      <Bar className={`${compact ? "h-1.5 w-8" : "h-2 w-16"} bg-volt/80`} />
      <div className="flex gap-1.5">
        <Bar className={`${compact ? "h-1 w-4" : "h-1.5 w-8"} bg-white/15`} />
        <Bar className={`${compact ? "h-1 w-4" : "h-1.5 w-8"} bg-white/15`} />
        <Bar className={`${compact ? "h-1 w-4" : "h-1.5 w-8"} bg-white/15`} />
      </div>
    </div>
    <div className={`mt-1 space-y-${compact ? "1" : "2"}`}>
      <Bar className={`${compact ? "h-2 w-4/5" : "h-3.5 w-4/5"} bg-white/85`} />
      <Bar className={`${compact ? "h-2 w-3/5" : "h-3.5 w-3/5"} bg-volt`} />
    </div>
    <div className="space-y-1">
      <Bar className={`${compact ? "h-1 w-full" : "h-1.5 w-full"} bg-white/15`} />
      <Bar className={`${compact ? "h-1 w-5/6" : "h-1.5 w-5/6"} bg-white/15`} />
    </div>
    <div className={`${compact ? "mt-1 h-4 w-14" : "mt-1 h-7 w-28"} rounded-full bg-volt shadow-[0_0_24px_rgba(0,240,255,0.4)]`} />
    {!compact && (
      <div className="mt-auto grid grid-cols-3 gap-2.5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-1.5 rounded-lg border border-white/[0.07] bg-white/[0.03] p-2.5">
            <Bar className="h-4 w-4 rounded-md bg-neon/70" />
            <Bar className="h-1.5 w-full bg-white/20" />
            <Bar className="h-1 w-2/3 bg-white/10" />
          </div>
        ))}
      </div>
    )}
  </div>
);

export const DeviceMockup = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div
      className="relative mx-auto w-full max-w-[560px] [perspective:1200px]"
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      data-testid="hero-device-mockup"
    >
      <div
        className="absolute -inset-10 rounded-full opacity-70 blur-3xl"
        style={{
          background:
            "radial-gradient(circle at 40% 30%, rgba(0,240,255,0.18), transparent 55%), radial-gradient(circle at 75% 75%, rgba(124,58,237,0.2), transparent 55%)",
        }}
        aria-hidden="true"
      />

      <motion.div
        initial={{ opacity: 0, y: 48, rotateX: 10 }}
        animate={{ opacity: 1, y: 0, rotateX: 0 }}
        transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative"
      >
        {/* Laptop */}
        <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-surface to-card p-1.5 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)]">
          <div className="overflow-hidden rounded-xl border border-white/[0.06] bg-ink">
            <div className="flex items-center gap-1.5 border-b border-white/[0.06] bg-card px-3 py-2">
              <span className="h-2 w-2 rounded-full bg-white/15" />
              <span className="h-2 w-2 rounded-full bg-white/15" />
              <span className="h-2 w-2 rounded-full bg-white/15" />
              <span className="ml-2 flex-1 rounded-md bg-white/[0.05] px-2 py-0.5 font-mono text-[9px] text-dim">
                yourbusiness.com
              </span>
            </div>
            <div className="aspect-[16/10]">
              <FakeSite />
            </div>
          </div>
        </div>
        <div className="mx-auto h-2.5 w-[104%] -translate-x-[2%] rounded-b-2xl rounded-t-sm bg-gradient-to-b from-surface to-[#0a0e1c]" />

        {/* Phone */}
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -bottom-8 -right-3 w-[110px] sm:-right-8 sm:w-[136px]"
          style={{ transform: "translateZ(60px)" }}
        >
          <div className="rounded-[22px] border border-white/15 bg-gradient-to-b from-surface to-card p-1 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.8)]">
            <div className="overflow-hidden rounded-[16px] bg-ink">
              <div className="mx-auto mt-1.5 h-1 w-8 rounded-full bg-white/15" />
              <div className="aspect-[9/16]">
                <FakeSite compact />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Lead notification toast */}
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.92 }}
          animate={{ opacity: [0, 1, 1, 0], y: [16, 0, 0, -8], scale: [0.92, 1, 1, 0.96] }}
          transition={{ duration: 5.5, times: [0, 0.12, 0.86, 1], repeat: Infinity, repeatDelay: 1.4, delay: 1.6 }}
          className="absolute -left-3 top-6 flex items-center gap-3 rounded-xl border border-volt/25 bg-card/90 px-4 py-3 shadow-[0_16px_48px_rgba(0,240,255,0.15)] backdrop-blur-md sm:-left-10"
          style={{ transform: "translateZ(90px)" }}
          data-testid="hero-lead-toast"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-volt/15 text-volt">
            <BellRing size={17} />
          </span>
          <span>
            <span className="block text-xs font-semibold text-white">New lead captured</span>
            <span className="block font-mono text-[10px] text-dim">Sarah booked a demo · just now</span>
          </span>
        </motion.div>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute -bottom-14 left-0 font-mono text-[10px] uppercase tracking-[0.25em] text-dim sm:-left-2"
      >
        Your site · working while you sleep
      </motion.p>
    </div>
  );
};
